import Tabs from "../components/Tabs";
import { useElemContext } from "../Contexts/ElemContext";
import { FaArrowUp, FaCheck, FaCog, FaUserSlash } from "react-icons/fa";
import { checkAdmin } from "../utils/jwtUtils";
import { useEffect, useState } from "react";
import Select from "react-select";
import { adultRank, childRank } from "../constants/belts";
import ContentContainer from "../components/ContentContainer";
import {
  createStudent,
  deleteStudent,
  patchStudent,
} from "../services/studentService";
import { useIsMobile } from "../hooks/useIsMobile";
import { useFeedback } from "../Contexts/FeedbackContext";
import {
  ERROR_CREATE,
  ERROR_DELETE,
  ERROR_UPDATE,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from "../constants/feedbacks";
import { PROMPT_DELETE_STUDENT } from "../constants/prompts";

export default function StudentPage() {
  const isMobile = useIsMobile();
  const { student, setStudent, setGroups } = useElemContext();
  const [isDisabled, setIsDisabled] = useState(true);
  const [nameState, setNameState] = useState("");
  const [surnameState, setSurnameState] = useState("");
  const [categoryState, setCategoryState] = useState("");
  const [rankState, setRankState] = useState("");
  const [rankOption, setRankOption] = useState([]);
  const { showFeedback } = useFeedback();

  const category = [
    { value: "enfant", label: "Enfant" },
    { value: "adulte", label: "Adulte" },
  ];

  useEffect(() => {
    setStudent(null);
  }, [setStudent]);

  useEffect(() => {
    setIsDisabled(true);
    if (student && student !== -1) {
      setRankState(student.rank?.level || "");
      setCategoryState(student.rank?.category || "");
      setNameState(student.name || "");
      setSurnameState(student.surname || "");
    } else if (student === -1) {
      setRankState("");
      setCategoryState("");
      setNameState("");
      setSurnameState("");
      setIsDisabled(false);
    }
  }, [student]);

  useEffect(() => {
    if (categoryState === "enfant") setRankOption(childRank);
    else if (categoryState === "adulte") setRankOption(adultRank);
  }, [categoryState]);

  function handleSettings() {
    setIsDisabled(!isDisabled);
  }

  function onSurnameChange(newValue) {
    setSurnameState(newValue);
  }
  function onNameChange(newValue) {
    setNameState(newValue);
  }
  function onCategoryChange(newValue) {
    setCategoryState(newValue);
  }
  function onRankChange(newValue) {
    setRankState(newValue);
  }

  function onSubmit() {
    const body = {
      rank_level: rankState,
      category: categoryState,
      name: nameState,
      surname: surnameState,
    };
    if (student !== -1) {
      patchStudent(student.id, body)
        .then(() => {
          showFeedback(SUCCESS_UPDATE, true);
          setIsDisabled(true);
          setGroups((prevGroups) => {
            const updatedGroups = { ...prevGroups };

            for (const groupName in updatedGroups) {
              const group = updatedGroups[groupName];

              const studentIndex = group.students.findIndex(
                (elem) => elem.id === student.id
              );

              if (studentIndex !== -1) {
                const updatedStudents = [...group.students];
                updatedStudents[studentIndex] = {
                  ...updatedStudents[studentIndex],
                  ...body,
                };

                updatedGroups[groupName] = {
                  ...group,
                  students: updatedStudents,
                };

                break;
              }
            }

            return updatedGroups;
          });
        })
        .catch((error) => {
          showFeedback(ERROR_UPDATE, false);

          console.error(error);
        });
    } else {
      createStudent(body)
        .then((response) => {
          showFeedback(SUCCESS_CREATE, true);
          setGroups((prevGroups) => {
            return {
              ...prevGroups,
              "Sans Groupe": {
                ...prevGroups["Sans Groupe"],
                students: [
                  ...prevGroups["Sans Groupe"].students,
                  response.data,
                ],
              },
            };
          });
        })
        .catch((error) => {
          showFeedback(ERROR_CREATE, false);
          console.error(error);
        });
    }
  }

  function handleDelete(studentId) {
    if (window.confirm(PROMPT_DELETE_STUDENT)) {
      deleteStudent(studentId)
        .then(() => {
          setGroups((prevGroups) => {
            const newGroups = { ...prevGroups };
            for (const groupName in newGroups) {
              const group = newGroups[groupName];

              const index = group.students.findIndex(
                (student) => student.id === studentId
              );

              if (index !== -1) {
                const updatedStudents = [...group.students];
                updatedStudents.splice(index, 1);
                newGroups[groupName] = {
                  ...group,
                  students: updatedStudents,
                };

                break;
              }
            }
            return newGroups;
          });
        })
        .catch((error) => {
          showFeedback(ERROR_DELETE, false);
          console.error(error);
        });
    }
  }

  return (
    <>
      {student == null && (
        <ContentContainer>
          <div className={`pl-5 pt-[10vh] ${isMobile ? "" : "hidden"}`}>
            <FaArrowUp />
            <h1 className="text-black text-xl">
              Selectionnez un élève à noter
            </h1>
          </div>
        </ContentContainer>
      )}
      {student !== null && (
        <ContentContainer>
          <div className="h-full flex justify-center items-center mt-20">
            <div className="grid grid-cols-5 gap-4 max-sm:grid-cols-2 gap-y-8">
              <LabelInput
                label="Nom"
                value={surnameState}
                inputdisabled={isDisabled}
                onChange={onSurnameChange}
              />
              <SelectInput
                label="Catégorie"
                values={category}
                selectDisabled={isDisabled}
                onChange={onCategoryChange}
                value={categoryState}
              />
              {!isDisabled && (
                <button
                  className="w-6 col-start-5 max-sm:col-start-3 place-self-center"
                  aria-label={"Check"}
                  onClick={() => onSubmit()}
                >
                  <FaCheck color="green" />
                </button>
              )}
              <LabelInput
                label="Prénom"
                value={nameState}
                inputdisabled={isDisabled}
                onChange={onNameChange}
              />
              <SelectInput
                label="Grade"
                values={rankOption}
                selectDisabled={isDisabled}
                onChange={onRankChange}
                value={rankState}
              />
              {checkAdmin() && (
                <>
                  <button
                    className="w-6 col-start-5 max-sm:col-start-3 place-self-center"
                    aria-label={"Settings"}
                    onClick={() => handleSettings()}
                  >
                    <FaCog />
                  </button>
                </>
              )}

              {checkAdmin() && (
                <IconButton
                  onClick={() => handleDelete(student.id)}
                  icon={<FaUserSlash />}
                  altText="Delete"
                />
              )}
            </div>
          </div>
          {student !== -1 && <Tabs student={student} />}
        </ContentContainer>
      )}
    </>
  );
}

const LabelInput = ({ label, value, inputdisabled, onChange }) => (
  <>
    <label className="col-start-1 place-self-end max-sm:place-self-center">
      {label}
    </label>
    <input
      type="text"
      disabled={inputdisabled}
      placeholder={label}
      value={value}
      className="border border-black px-1"
      onChange={(e) => onChange(e.target.value)}
    />
  </>
);

const SelectInput = ({ label, values, selectDisabled, onChange, value }) => (
  <>
    <label className="col-start-3 max-sm:col-start-1 place-self-end max-sm:place-self-center">
      {label}
    </label>

    <Select
      options={values}
      isDisabled={selectDisabled}
      value={values.find((rank) => rank.value === value)}
      className={"text-black"}
      onChange={(e) => onChange(e.value)}
    />
  </>
);

const IconButton = ({ icon, altText, onClick }) => (
  <button
    className="w-6 col-start-5 max-sm:col-start-3 place-self-center"
    aria-label={altText}
    onClick={onClick}
  >
    {icon}
  </button>
);
